import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardContent } from '@/components/ui/Card';
import { ContextMenu } from '@/components/ui/ContextMenu';
import type { ColumnDef } from '@tanstack/react-table';
import { AvailabilityReportItem, InventoryItem, InventoryListParams } from '@/types';
import { getColumnId } from '@/types/table.types';
import {
  useAvailabilityColumnStore,
  useAvailabilityStore,
  useInventoryDataStore,
  useInventoryFilterStore,
  useTradesDataStore,
  useUIStore,
} from '@/stores';
import { useCellSelectionWithCopy } from '@/hooks/useCellSelectionWithCopy';
import { useAvailabilityReport } from '@/hooks';
import { computeVisualColIds } from '@/utils/cell-selection-utils';
import {
  AVAILABILITY_COLUMN_ID_TO_FILTER_ID,
  AVAILABILITY_FILTERABLE_COLUMN_IDS,
  formatBenchmarkFee,
  getActiveAvailabilityFilterColumnIds,
} from '@/utils/availability';
import { getAppliedAvailabilityFilterChips } from '@/utils/availability/filter-chip-labels';
import type { AvailabilityFilterOptionSets } from '@/utils/availability/filter-chip-labels';
import { calculateAvailabilityColumnWidths } from '@/utils/availability/column-width';
import { useAvailabilityContextMenus } from '@/features/availability/hooks/useAvailabilityContextMenus';
import { AppliedFiltersStrip } from '../AppliedFiltersStrip';
import { TourSentinel } from '@/features/onboarding';
import { RefreshStatusLoader } from '../RefreshStatusLoader';
import { ScrollToTopButton } from '../ScrollToTopButton';
import { AvailabilityTable } from '../tables/AvailabilityTable';
import { useAuth } from '@/hooks/auth/use-auth';
import { getStoredAvailabilityWidths } from '../tables/AvailabilityTableSkeleton';

type AvailabilityTabPanelProps = {
  apiParams: Omit<InventoryListParams, 'limit' | 'offset'>;
  totalCount: number;
  allInventoryData: InventoryItem[] | undefined;
  appliedFiltersExpanded: boolean;
  onAppliedFiltersExpandedChange: (v: boolean) => void;
  onAppliedFiltersCountChange: (n: number) => void;
  onSearchResultCountChange: (n: number) => void;
  onLoadingStateChange: (state: { isLoading: boolean; hasError: boolean }) => void;
  onSwitchToInventory: () => void;
};

export const AvailabilityTabPanel: React.FC<AvailabilityTabPanelProps> = ({
  apiParams,
  totalCount,
  allInventoryData,
  appliedFiltersExpanded,
  onAppliedFiltersExpandedChange,
  onAppliedFiltersCountChange,
  onSearchResultCountChange,
  onLoadingStateChange,
  onSwitchToInventory,
}) => {
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const sortedDataRef = useRef<AvailabilityReportItem[]>([]);
  const rowCountRef = useRef(0);

  const searchQuery = useInventoryDataStore((state) => state.searchQuery);
  const setSearchQuery = useInventoryDataStore((state) => state.setSearchQuery);
  const availabilitySorting = useInventoryDataStore((state) => state.availabilitySorting);
  const setAvailabilitySorting = useInventoryDataStore((state) => state.setAvailabilitySorting);

  const [availabilitySortingArr, setAvailabilitySortingArr] = useState<{ id: string; desc: boolean }[]>(
    availabilitySorting ? [availabilitySorting] : []
  );

  useEffect(() => {
    setAvailabilitySortingArr(availabilitySorting ? [availabilitySorting] : []);
  }, [availabilitySorting]);

  const handleAvailabilitySortChange = useCallback(
    (arr: { id: string; desc: boolean }[]) => {
      setAvailabilitySortingArr(arr);
      setAvailabilitySorting(arr.length > 0 ? arr[0] : null);
    },
    [setAvailabilitySorting]
  );

  const setFilterSidebarCollapsed = useUIStore((state) => state.setFilterSidebarCollapsed);
  const setFocusedFilterId = useUIStore((state) => state.setFocusedFilterId);
  const expandFilter = useUIStore((state) => state.expandFilter);
  const locateHighlightedColumnIds = useUIStore((state) => state.locateHighlightedColumnIds);
  const isColumnsTabActive = useUIStore((state) => state.columnsTabActive);
  const dateTimePickerOpen = useUIStore((state) => state.dateTimePickerOpen);
  const highlightedColumnIds = locateHighlightedColumnIds ?? [];

  const isForceCollecting = useInventoryDataStore((state) => state.isForceCollecting);
  const isForceCollectSuccess = useInventoryDataStore((state) => state.isForceCollectSuccess);

  const availabilityFilters = useAvailabilityStore(
    (state) =>
      state.availabilityFilters || {
        benchmarkFeeThreshold: 0,
      }
  );
  const setAvailabilityFilters = useAvailabilityStore((state) => state.setAvailabilityFilters);
  const resetLocalFilter = useAvailabilityStore((state) => state.resetLocalFilter);
  const clearAvailabilityFilters = useAvailabilityStore((state) => state.clearAvailabilityFilters);
  const setFilteredAvailabilityData = useAvailabilityStore((state) => state.setFilteredAvailabilityData);
  const filterMetadata = useInventoryFilterStore((state) => state.filterMetadata);

  const columnConfig = useAvailabilityColumnStore((state) => state.columns);
  const pinnedLeft = useAvailabilityColumnStore((state) => state.pinnedLeft);
  const pinnedRight = useAvailabilityColumnStore((state) => state.pinnedRight);
  const pinColumn = useAvailabilityColumnStore((state) => state.pinColumn);
  const getPinState = useAvailabilityColumnStore((state) => state.getPinState);
  const toggleColumnVisibility = useAvailabilityColumnStore((state) => state.toggleColumnVisibility);

  const setSelectedItemForTrade = useTradesDataStore((state) => state.setSelectedItemForTrade);
  const setIsCreateTradeModalOpen = useTradesDataStore((state) => state.setIsCreateTradeModalOpen);

  const {
    data: availabilityData,
    isLoading,
    error,
  } = useAvailabilityReport(
    apiParams,
    totalCount,
    availabilityFilters.benchmarkFeeThreshold ?? 0,
    apiParams.state !== undefined && totalCount > 0
  );

  const columns = useMemo((): ColumnDef<AvailabilityReportItem, unknown>[] => {
    const columnDefs: Record<string, ColumnDef<AvailabilityReportItem, unknown>> = {
      rowNumber: { id: 'rowNumber', header: '', size: 40 },
      symbol_id: { id: 'symbol_id', accessorKey: 'symbol_id', header: 'Symbol ID' },
      sedol: { id: 'sedol', accessorKey: 'sedol', header: 'SEDOL' },
      isin: { id: 'isin', accessorKey: 'isin', header: 'ISIN' },
      quantity: { id: 'quantity', accessorKey: 'quantity', header: 'Quantity' },
      benchmark_fee: { id: 'benchmark_fee', accessorKey: 'benchmark_fee', header: 'Benchmark Fee', minSize: 140 },
      ca: { id: 'ca', accessorKey: 'ca', header: 'CA', size: 50 },
      transfers: { id: 'transfers', accessorKey: 'transfers', header: 'Transfers', minSize: 110 },
      counterparties: { id: 'counterparties', accessorKey: 'counterparties', header: 'Counterparties' },
    };

    const cols: ColumnDef<AvailabilityReportItem, unknown>[] = [];
    cols.push(columnDefs['rowNumber']);
    for (const cfg of columnConfig) {
      if (cfg.id === 'rowNumber') continue;
      if (!cfg.visible) continue;
      const def = columnDefs[cfg.id];
      if (def) cols.push(def);
    }
    return cols;
  }, [columnConfig]);

  const activeFilterColumnIds = useMemo(
    () => getActiveAvailabilityFilterColumnIds(availabilityFilters),
    [availabilityFilters]
  );

  const handleFilterColumnClick = useCallback(
    (columnId: string) => {
      setFilterSidebarCollapsed(false);
      const filterId = AVAILABILITY_COLUMN_ID_TO_FILTER_ID[columnId] ?? columnId;
      setFocusedFilterId(filterId);
      expandFilter(filterId);
    },
    [setFilterSidebarCollapsed, setFocusedFilterId, expandFilter]
  );

  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);
  const colIds = useMemo(
    () => computeVisualColIds(columns, pinnedLeftSet, pinnedRightSet),
    [columns, pinnedLeftSet, pinnedRightSet]
  );

  const formatValue = useCallback((item: AvailabilityReportItem, colId: string) => {
    if (colId === 'counterparties') {
      return item.counterparties.map((cp) => `${cp.name}: ${cp.quantity}`).join(', ');
    }
    if (colId === 'benchmark_fee') {
      // Copies must match the rendered cell: two decimals (LR-599).
      return formatBenchmarkFee(item.benchmark_fee);
    }
    if (colId === 'transfers') {
      return item.pending_transfers > 0 ? `Out: ${item.pending_transfers}` : '-';
    }
    const value = item[colId as keyof AvailabilityReportItem];
    return value == null ? '' : String(value);
  }, []);

  const selection = useCellSelectionWithCopy<AvailabilityReportItem>({
    colIds,
    rowCountRef,
    scrollContainerRef: parentRef,
    rowHeight: 39,
    headerHeight: 41,
    tableSelector: '[data-availability-table-scroll-container]',
    sortedDataRef,
    formatValue,
    getHeader: (colId) => columnConfig.find((c) => c.id === colId)?.label ?? colId,
  });

  useEffect(() => {
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: clear only when sort/filter changes; depending on the whole `selection` object would refire every time the selection changes (defeating the purpose)
  }, [availabilitySortingArr, availabilityFilters, selection.clearSelection]);

  const {
    headerCtxMenu,
    headerCtxMenuItems,
    handleColumnContextMenu,
    closeHeaderCtxMenu,
    cellCtxMenu,
    cellCtxMenuItems,
    handleCellContextMenu,
    closeCellCtxMenu,
    bindScrollClose,
  } = useAvailabilityContextMenus({
    columnConfig,
    columns,
    sorting: availabilitySortingArr,
    setSorting: handleAvailabilitySortChange,
    getPinState,
    pinColumn,
    toggleColumnVisibility,
    handleFilterColumnClick,
    availabilityFilters,
    setAvailabilityFilters,
    sortedDataRef,
    colIds,
    selectedCellsForCopy: selection.getSelectedCellsAsSet,
    selectedCellCount: selection.getSelectedCellCount,
    selectColumn: selection.selectColumn,
    deselectColumn: selection.deselectColumn,
    isColumnFullySelected: selection.isColumnFullySelected,
    colIdToIndex: selection.colIdToIndex,
  });

  const availabilityOptionSets = useMemo<AvailabilityFilterOptionSets>(
    () => ({
      counterparties: filterMetadata.counterparties,
      caStatuses: filterMetadata.caStatuses,
    }),
    [filterMetadata]
  );
  const appliedFilterChips = useMemo(
    () => getAppliedAvailabilityFilterChips(availabilityFilters, availabilityOptionSets),
    [availabilityFilters, availabilityOptionSets]
  );
  const appliedFiltersCount = appliedFilterChips.length;

  const handleAppliedChipClick = useCallback(
    (filterId: string) => {
      setFilterSidebarCollapsed(false);
      setFocusedFilterId(filterId);
      expandFilter(filterId);
    },
    [setFilterSidebarCollapsed, setFocusedFilterId, expandFilter]
  );

  const handleAppliedChipRemove = useCallback(
    (filterId: string) => {
      resetLocalFilter(filterId);
    },
    [resetLocalFilter]
  );

  const sortedData = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0) return [];

    let filtered = [...availabilityData];

    if (availabilityFilters.benchmarkFeeThreshold !== null) {
      filtered = filtered.filter((item) => item.benchmark_fee >= availabilityFilters.benchmarkFeeThreshold!);
    }

    if (availabilityFilters.quantityMin !== null) {
      filtered = filtered.filter((item) => item.quantity >= availabilityFilters.quantityMin!);
    }

    if (availabilityFilters.quantityMax !== null) {
      filtered = filtered.filter((item) => item.quantity <= availabilityFilters.quantityMax!);
    }

    if (availabilityFilters.counterparties && availabilityFilters.counterparties.length > 0) {
      filtered = filtered.filter((item) =>
        item.counterparties.some((cp) => availabilityFilters.counterparties.includes(cp.name))
      );
    }

    if (availabilityFilters.caStatuses && availabilityFilters.caStatuses.length > 0) {
      // Match on the grouped row's CA *statuses*: the filter options are
      // statuses (Upcoming, Pending, …) while `item.ca` holds the event
      // type shown in the CA column — comparing against it never matched
      // anything (LR-597). Same any-of semantics as the Inventory tab.
      filtered = filtered.filter((item) =>
        item.ca_statuses.some((status) => availabilityFilters.caStatuses.includes(status))
      );
    }

    if (availabilityFilters.excludeUpcomingCA) {
      filtered = filtered.filter((item) => !item.ca || item.ca === 'None' || item.ca === '-');
    }

    if (availabilityFilters.hideISINNone) {
      filtered = filtered.filter(
        (item) => item.isin && item.isin.toLowerCase() !== 'none' && item.isin !== '-'
      );
    }

    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      const query = trimmedQuery.toLowerCase();

      filtered = filtered.filter((item) => {
        return (
          item.symbol_id.toLowerCase().includes(query) ||
          item.sedol.toLowerCase().includes(query) ||
          item.isin.toLowerCase().includes(query) ||
          item.quantity.toString().includes(query) ||
          item.benchmark_fee.toString().includes(query) ||
          item.ca.toLowerCase().includes(query) ||
          item.transfers.toLowerCase().includes(query) ||
          item.counterparties.some(
            (cp) => cp.name.toLowerCase().includes(query) || cp.quantity.toString().includes(query)
          )
        );
      });
    }

    if (availabilitySorting === null) {
      return filtered;
    }

    const sorted = filtered.sort((a, b) => {
      const columnId = availabilitySorting.id;
      let aValue: string | number | null | undefined;
      let bValue: string | number | null | undefined;

      switch (columnId) {
        case 'symbol_id':
          aValue = a.symbol_id;
          bValue = b.symbol_id;
          break;
        case 'sedol':
          aValue = a.sedol;
          bValue = b.sedol;
          break;
        case 'isin':
          aValue = a.isin;
          bValue = b.isin;
          break;
        case 'quantity':
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case 'adjusted_quantity':
          aValue = a.adjusted_quantity;
          bValue = b.adjusted_quantity;
          break;
        case 'benchmark_fee':
          aValue = a.benchmark_fee;
          bValue = b.benchmark_fee;
          break;
        case 'ca':
          aValue = a.ca;
          bValue = b.ca;
          break;
        case 'transfers':
          aValue = a.pending_transfers;
          bValue = b.pending_transfers;
          break;
        default:
          return 0;
      }

      if (aValue === null || aValue === undefined) {
        if (bValue === null || bValue === undefined) return 0;
        return 1;
      }
      if (bValue === null || bValue === undefined) {
        return -1;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const diff = aValue - bValue;
        return availabilitySorting.desc ? -diff : diff;
      }

      const stringA = String(aValue).toLowerCase();
      const stringB = String(bValue).toLowerCase();
      if (stringA < stringB) return availabilitySorting.desc ? 1 : -1;
      if (stringA > stringB) return availabilitySorting.desc ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [availabilityData, availabilitySorting, searchQuery, availabilityFilters]);

  sortedDataRef.current = sortedData;
  rowCountRef.current = sortedData.length;

  useEffect(() => {
    setFilteredAvailabilityData(sortedData);
  }, [sortedData, setFilteredAvailabilityData]);

  useEffect(() => {
    onAppliedFiltersCountChange(appliedFiltersCount);
  }, [appliedFiltersCount, onAppliedFiltersCountChange]);

  useEffect(() => {
    onSearchResultCountChange(sortedData.length);
  }, [sortedData.length, onSearchResultCountChange]);

  useEffect(() => {
    onLoadingStateChange({ isLoading, hasError: !!error });
  }, [isLoading, error, onLoadingStateChange]);

  useEffect(() => {
    return bindScrollClose(parentRef.current);
  }, [bindScrollClose]);

  const widthsColumnsKeyRef = useRef<string>('');
  const columnWidthsRef = useRef<Record<string, number> | null>(null);
  const columnWidths = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0 || columns.length === 0) {
      return columnWidthsRef.current ?? getStoredAvailabilityWidths();
    }
    const columnsKey = columns.map((c) => getColumnId(c)).join(',');
    if (!columnWidthsRef.current || columnsKey !== widthsColumnsKeyRef.current) {
      widthsColumnsKeyRef.current = columnsKey;
      columnWidthsRef.current = calculateAvailabilityColumnWidths(columns, availabilityData);
    }
    return columnWidthsRef.current;
  }, [columns, availabilityData]);

  const scrollToTop = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  // LR-257: only surface the per-row Trade Entry action to users who can create trades.
  const { hasPermission } = useAuth();
  const canTrade = hasPermission('create_trades');

  const handleTradeEntry = useCallback(
    (item: AvailabilityReportItem) => {
      if (!allInventoryData) return;
      const matchingItem = allInventoryData.find((inv) => inv.symbol_id === item.symbol_id);
      if (matchingItem) {
        if (item.counterparties.length > 0) {
          const firstCounterparty = item.counterparties[0];
          const itemWithCounterparty: InventoryItem = {
            ...matchingItem,
            counterparty: firstCounterparty.name,
          };
          setSelectedItemForTrade(itemWithCounterparty);
        } else {
          setSelectedItemForTrade(matchingItem);
        }
        setIsCreateTradeModalOpen(true);
      }
    },
    [allInventoryData, setSelectedItemForTrade, setIsCreateTradeModalOpen]
  );

  return (
    <>
      <TourSentinel name="availability-overview" />
      <AppliedFiltersStrip
        count={appliedFiltersCount}
        chips={appliedFilterChips}
        onChipClick={handleAppliedChipClick}
        onChipRemove={handleAppliedChipRemove}
        onClearAll={clearAvailabilityFilters}
        hideTrigger
        expanded={appliedFiltersExpanded}
        onToggleExpanded={onAppliedFiltersExpandedChange}
      />
      <CardContent className="flex flex-1 flex-col overflow-hidden p-0 border-t border-gray-200 relative">
        <AvailabilityTable
          data={sortedData}
          columns={columns}
          sorting={availabilitySortingArr}
          onSortChange={handleAvailabilitySortChange}
          searchQuery={searchQuery}
          columnWidths={columnWidths}
          isLoading={isLoading}
          error={error}
          hoveredColumn={hoveredColumn}
          onHoveredColumnChange={setHoveredColumn}
          onShowScrollTop={setShowScrollTop}
          onSwitchToInventory={onSwitchToInventory}
          onSearchQueryChange={setSearchQuery}
          scrollContainerRef={parentRef}
          accountForClientTransfers={availabilityFilters.accountForClientTransfers}
          onTradeEntry={canTrade ? handleTradeEntry : undefined}
          pinnedLeft={pinnedLeft}
          pinnedRight={pinnedRight}
          onPinColumn={(colId, side) => pinColumn(colId, side)}
          activeFilterColumnIds={activeFilterColumnIds}
          onFilterColumnClick={handleFilterColumnClick}
          filterableColumnIds={AVAILABILITY_FILTERABLE_COLUMN_IDS}
          onContextMenuColumn={handleColumnContextMenu}
          onContextMenuCell={handleCellContextMenu}
          isCellSelected={selection.isCellSelected}
          isCellActive={selection.isCellActive}
          isRowSelected={selection.isRowSelected}
          isColSelected={selection.isColSelected}
          getCellBorders={selection.getCellBorders}
          colIdToIndex={selection.colIdToIndex}
          selectionTrigger={selection.selection}
          ensureCellSelectedForContextMenu={selection.ensureCellSelectedForContextMenu}
          onCellMouseDown={selection.handleCellMouseDown}
          onCellMouseEnter={selection.handleCellMouseEnter}
          onCellMouseUp={selection.handleCellMouseUp}
          isColumnsTabActive={isColumnsTabActive}
          highlightedColumnIds={highlightedColumnIds}
        />
        <RefreshStatusLoader
          isVisible={(isForceCollecting || isForceCollectSuccess) && !dateTimePickerOpen}
          isSuccess={isForceCollectSuccess}
          isBackToTopVisible={showScrollTop}
        />
      </CardContent>
      <ScrollToTopButton show={showScrollTop} onClick={scrollToTop} />
      {headerCtxMenu && (
        <ContextMenu
          items={headerCtxMenuItems}
          position={{ x: headerCtxMenu.x, y: headerCtxMenu.y }}
          onClose={closeHeaderCtxMenu}
        />
      )}
      {cellCtxMenu && (
        <ContextMenu
          items={cellCtxMenuItems}
          position={{ x: cellCtxMenu.x, y: cellCtxMenu.y }}
          onClose={closeCellCtxMenu}
        />
      )}
    </>
  );
};
